import { FlexBox } from 'components/Containers/FlexBox';
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Line,
} from 'recharts';
import { Box, Typography } from '@mui/material';
import type { Asset } from 'types/asset.types';
import type { DataEntry, Data, HistogramData } from 'types/FleetOverview.types';

import { useState, useEffect } from 'react';
import { Selector } from 'components/Form/Selectors/Selector';
import { findHighestAndValuesForKey } from '../FleetOverview/helpers/asset.helpers';
import { findHighestAndLowestCumulativeTotal } from './helper';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

export const ChartView = ({
  enableDarkTheme,
  assets,
  metrics,
}: {
  enableDarkTheme: boolean;
  assets: Asset[];
  metrics: Data;
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const [assetBumperIds, setAssetBumperIds] = useState<string[]>([]);
  const [data, setData] = useState<any[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<string>('');

  const colorIndex = ['#F08733', '#58BF6A', '#497BFA'];

  useEffect(() => {
    const formattedMetrics: any[] = [];
    const newAssetBumperIdsSet: Set<string> = new Set<string>();

    for (const key in metrics) {
      let obj = {
        name: key.replace(/\b\w/g, (char) => char.toUpperCase()),
      };
      const value = (metrics as any)[key] as {
        data: DataEntry[];
        histogram: HistogramData[];
      };
      const benchmarkValue =
        value.data.find((item) => item.device === 'benchmark')?.value ?? 0;

      value.data.forEach((item) => {
        const currentAsset = assets.find(
          (asset) => asset.device.deviceId === item.device
        );
        if (currentAsset) {
          newAssetBumperIdsSet.add(currentAsset.bumperNumber);
          obj = {
            ...obj,
            [currentAsset?.bumperNumber]: Math.abs(
              percentageDifference(benchmarkValue, item.value)
            ),
          };
        }
      });

      formattedMetrics.push(obj);
    }

    const newAssetBumperIds = Array.from(newAssetBumperIdsSet).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );

    const mergedArr = findHighestAndValuesForKey(
      formattedMetrics,
      newAssetBumperIds[0]
    );

    setData(mergedArr);
    setAssetBumperIds(newAssetBumperIds);
    setSelectedAsset(newAssetBumperIds[0]);
  }, [metrics, assets]);

  useEffect(() => {
    if (selectedAsset !== '') {
      const mergedArr = findHighestAndValuesForKey(data, selectedAsset);

      setData(mergedArr);
    }
  }, [selectedAsset]);

  const selectorOptions = assetBumperIds
    .map((item) => {
      return {
        id: item,
        display: item,
      };
    })
    .sort((a, b) =>
      a.id.localeCompare(b.id, undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    );

  const { highest, lowest } = findHighestAndLowestCumulativeTotal(data);

  return (
    <Box>
      <Box
        sx={{
          paddingBottom: '20px',
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            paddingRight: '5px',
          }}
        >
          <Typography
            sx={{
              color: palette.textPrimary,
              paddingTop: '10px',
              paddingBottom: '10px',
            }}
          >
            {t('pareto_chart')}
          </Typography>

          <Box display="flex" alignItems="center">
            {assetBumperIds?.map((item, index) => {
              return (
                <>
                  <Box width={10} height={10} bgcolor={colorIndex[index]} />
                  <Typography
                    sx={{
                      fontSize: '11px',
                      paddingLeft: '5px',
                      paddingRight: '10px',
                      color: palette.textPrimary,
                    }}
                    key={index}
                  >
                    {item}
                  </Typography>
                </>
              );
            })}
            <Typography
              sx={{
                paddingLeft: '20px',
                paddingRight: '20px',
                color: palette.textPrimary,
                fontSize: '12px',
              }}
            >
              {t('pareto_primary')}
            </Typography>

            <Selector
              minWidth="1px"
              value={selectedAsset}
              onChange={(event) => {
                setSelectedAsset(String(event.target.value));
              }}
              selectorOptions={selectorOptions}
              enableDarkTheme={enableDarkTheme}
            />
          </Box>
        </Box>
      </Box>
      <FlexBox
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          maxWidth: 'inherit',
          height: '500px',
          flexDirection: 'column',
          paddingTop: '20px',
          marginRight: '10px',
          paddingBottom: '10px',
        }}
        id="vehicle-status-page"
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            width={700}
            height={300}
            data={data}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 30,
            }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={palette.cartesianGrid}
              vertical={false}
            />
            <XAxis
              dataKey="name"
              label={{
                value: 'Metric',
                fill: enableDarkTheme
                  ? palette.textPrimary
                  : palette.textPrimary,
                dy: 30,
                dx: 0,
              }}
            />
            <YAxis
              orientation="left"
              yAxisId="left"
              domain={[0, 'dataMax']}
              label={{
                value: 'Change from benchmark (%)',
                angle: -90,
                position: 'inside',
                fill: enableDarkTheme
                  ? palette.textPrimary
                  : palette.textPrimary,
                dy: 0,
                dx: -15,
              }}
            />
            <YAxis
              orientation="right"
              yAxisId="right"
              label={{
                value: 'Cumulative Total (%) (Pareto Primary)',
                angle: -90,
                position: 'inside',
                fill: enableDarkTheme
                  ? palette.textPrimary
                  : palette.textPrimary,
                dy: 0,
                dx: 15,
              }}
              domain={[lowest ?? 0, highest ?? 100]}
            />
            <Tooltip
              formatter={(value, name) => {
                if (name === 'Cumulative Total') {
                  return [`${value}%`, name];
                }
                return [`${value}%`, `${name} (abs. diff from benchmark)`];
              }}
            />
            {assetBumperIds.map((item, index) => {
              return (
                <Bar
                  dataKey={item}
                  fill={colorIndex[index]}
                  key={index}
                  yAxisId="left"
                />
              );
            })}
            <Line
              type="monotone"
              activeDot={{ r: 8 }}
              dataKey="Cumulative Total"
              yAxisId="right"
              stroke={
                colorIndex[
                  selectorOptions.findIndex((item) => item.id === selectedAsset)
                ]
              }
            />
          </ComposedChart>
        </ResponsiveContainer>
      </FlexBox>
    </Box>
  );
};

function percentageDifference(
  benchmarkValue: number,
  actualValue: number
): number {
  if (benchmarkValue === 0 && actualValue === 0) {
    return 0;
  }

  if (benchmarkValue === 0) {
    return 100;
  }

  const percentageChange =
    Math.abs((actualValue - benchmarkValue) / benchmarkValue) * 100;
  return parseFloat(percentageChange.toFixed(2));
}
